import { fabric } from "sunzi-fabric";
import clonedeep from "lodash.clonedeep";
import { Font } from "@/sunzi";
import { loadImageFromURL, loadFont, base64ToBlob, slim7n } from "@/utils";
import {
  SockDataConnect,
  SockDataImage,
  SockDataLineText,
  SockBottomImage,
  SockData,
  ImageData,
} from "./new-sock";
import { urlToBlob } from "@sunzi/utils";

interface Options {
  [propName: string]: {
    size: string;
    // value: string;
    textVersion?: SockDataConnect;
    imageVersion: SockDataConnect;
  };
}

type SelectedType = {
  bottomImage: string;
  size: string;
};

class Pattern {
  // fabric canvas 对象
  private _canvas: fabric.Canvas;
  // 底图缓存
  private _bottomImageCache: {
    [propName: string]: {
      [propName: string]: fabric.Image;
    };
  };
  // 底图 src
  private _bottomImageSrc: { [propName: string]: SockBottomImage[] };
  // 字体缓存
  private _fontCache: boolean;
  // 字体名称
  private _fontNames: string[];
  // 不同 size 选项下对应的数据
  public options: Options;
  // 当前选择的底图 id
  public bottomImageSelected: string;
  // 当前选择的 size id
  public sizeSelected: string;
  // 是否刻字
  public isLettering: boolean;
  // 旋转后的头像坐标
  public avatarData?: SockDataConnect;

  constructor(
    sockDatas: SockData[],
    selected: SelectedType,
    isLettering: boolean = false,
  ) {
    const _element = document.createElement("canvas");
    this._canvas = new fabric.Canvas(_element);
    this._bottomImageCache = {};
    this._bottomImageSrc = {};
    this.bottomImageSelected = selected.bottomImage;
    this.sizeSelected = selected.size;
    this.isLettering = isLettering;
    this._fontCache = false;
    this._fontNames = [];

    this.options = {};
    for (let i = 0; i < sockDatas.length; i++) {
      let sockData = sockDatas[i];
      this._bottomImageSrc[sockData.id] = sockData.bottomImages;
      this.options[sockData.id] = {
        size: sockData.size,
        imageVersion: {
          images: [],
        },
      };
      for (let j = 0; j < sockData.connects.length; j++) {
        let connect = sockData.connects[j];
        if (connect.hasOwnProperty("lineTexts")) {
          this.options[sockData.id].textVersion = {
            images: connect.images,
            lineTexts: connect.lineTexts,
          };
          if (!this._fontCache && connect.lineTexts) {
            connect.lineTexts.map((item) => {
              this._fontNames.push(item.font.name);
              item.fonts?.map((font) => {
                this._fontNames.push(font.name);
              });
            });
          }
        } else {
          this.options[sockData.id].imageVersion = {
            images: connect.images,
          };
        }
      }
    }
  }

  /** 缓存底图 */
  public cacheImage = async () => {
    for (let sizeId in this._bottomImageSrc) {
      const bottomImage = this._bottomImageSrc[sizeId];
      this._bottomImageCache[sizeId] = {};
      bottomImage &&
        bottomImage.forEach(async (item) => {
          this._bottomImageCache[sizeId][item.id] = await loadImageFromURL(
            slim7n(item.image),
          );
        });
    }
  };

  /** 缓存字体 */
  public cacheFonts = async () => {
    await Promise.all(
      this._fontNames.map(async (item, index) => {
        await loadFont(item).then(() => {
          if (index + 1 === this._fontNames.length) {
            this._fontCache = true;
          }
        });
      }),
    );
  };

  /** 获取当前图片块的数量 */
  public getImageCount = () => {
    let sockImages: SockDataImage[] = [];
    if (this.isLettering)
      sockImages =
        this.options[this.sizeSelected].textVersion?.images ??
        this.options[this.sizeSelected].imageVersion.images;
    else sockImages = this.options[this.sizeSelected].imageVersion.images;
    return sockImages.length;
  };

  /** 加载底图 */
  private _renderBottomImage = () =>
    new Promise<void>((resolve) => {
      const image =
        this._bottomImageCache[this.sizeSelected][this.bottomImageSelected];
      const imageHeight = image.height as number;
      const imageWidth = image.width as number;
      // 底图最大值为 2000
      if (imageHeight > 2000 || imageWidth > 2000) {
        let width = 2000;
        let height =
          (2000 * (image.height as number)) / (image.width as number);
        image.scaleToWidth(2000);
        if ((image.width as number) < (image.height as number)) {
          height = 2000;
          width = (2000 * (image.width as number)) / (image.height as number);
          image.scaleToHeight(2000);
        }
        this._canvas.setDimensions({
          width: width,
          height: height,
        });
      } else {
        this._canvas.setDimensions({
          width: imageWidth,
          height: imageHeight,
        });
      }
      this._canvas.setBackgroundImage(image, () => {
        this._canvas.renderAll();
        resolve();
      });
    });

  /** 加载图片块 */
  private _renderImages = async (imagesBase64: Omit<ImageData, "origin">[]) => {
    const allBlobURL: string[] = [];
    // 同时加载所有的图片
    const images = await Promise.all(
      imagesBase64.map(async (item) => {
        // XXX: 使用@sunzi/utils myFaceCut方法返回数据为线上链接，直接转成blob
        const blobURL = URL.createObjectURL(await urlToBlob(item.image));
        allBlobURL.push(blobURL);
        return await loadImageFromURL(blobURL);
      }),
    );
    let sockImages: SockDataImage[] = [];
    if (this.isLettering) {
      // 如果有刻字版本就拿刻字，没有就拿没刻字的版本
      sockImages =
        this.options[this.sizeSelected].textVersion?.images ??
        this.options[this.sizeSelected].imageVersion.images;
      this.avatarData = clonedeep(
        this.options[this.sizeSelected].textVersion ??
          this.options[this.sizeSelected].imageVersion,
      );
    } else {
      sockImages = this.options[this.sizeSelected].imageVersion.images;
      this.avatarData = clonedeep(this.options[this.sizeSelected].imageVersion);
    }
    sockImages.forEach((item, index) => {
      URL.revokeObjectURL(allBlobURL[index]);
      const i = index % images.length;
      const image = clonedeep<fabric.Image>(images[i]);
      const { width = item.width, height = item.height } = image;
      const bottomImageWidth = this._bottomImageCache[this.sizeSelected][
        this.bottomImageSelected
      ].width as number;
      const bottomImageHeight = this._bottomImageCache[this.sizeSelected][
        this.bottomImageSelected
      ].height as number;
      const canvasWidth = this._canvas.getWidth();
      const canvasHeight = this._canvas.getHeight();
      const canvasScale = Math.min(
        canvasWidth / bottomImageWidth,
        canvasHeight / bottomImageHeight,
      );
      if (imagesBase64[i].faceRect) {
        const faceWidth = item.width * canvasScale;
        const faceHeight = item.height * canvasScale;
        const faceRect = imagesBase64[i].faceRect!;
        const scale = Math.min(
          faceWidth / faceRect.width,
          faceHeight / faceRect.height,
        );
        const heightDistance =
          ((image.height as number) - faceRect.top - faceRect.height) * scale;
        const imageTop =
          (item.top + item.height) * canvasScale + heightDistance;
        const imageLeft =
          item.left * canvasScale -
          faceRect.left * scale +
          ((image.width as number) / 2) * scale;
        image.set({
          left: imageLeft,
          top: imageTop,
          originX: "center",
          originY: "bottom",
          scaleX: scale,
          scaleY: scale,
          hasControls: false,
          selectable: false,
        });
        const imageLeftTopCoord = image._getLeftTopCoords();
        this.avatarData!.images[index].left = imageLeftTopCoord.x;
        this.avatarData!.images[index].top = imageLeftTopCoord.y;
      } else {
        const scale =
          Math.min(item.width / width, item.height / height) * canvasScale;
        image.set({
          left: item.left * canvasScale,
          top: item.top * canvasScale,
          scaleX: scale,
          scaleY: scale,
          // angle: item.angle,
          hasControls: false,
          selectable: false,
          // originX: 'center',
          // originY: 'center'
        });
      }
      image.rotate(item.angle);
      this._canvas.add(image);
      this._canvas.renderAll();
    });
  };

  /** 加载直线刻字块 */
  private _renderLineText = async (
    lineText: string,
    fontColor?: string,
    font?: Font,
  ) => {
    let sockLines: SockDataLineText[] | undefined =
      this.options[this.sizeSelected].textVersion?.lineTexts;

    sockLines?.forEach(async (item) => {
      if (!this._fontCache) {
        try {
          await loadFont(item.font.name, 6000);
          this._fontCache = true;
        } catch (e) {
          console.error(item.font.name + "字体加载超时");
        }
      }
      const bottomImageWidth = this._bottomImageCache[this.sizeSelected][
        this.bottomImageSelected
      ].width as number;
      const bottomImageHeight = this._bottomImageCache[this.sizeSelected][
        this.bottomImageSelected
      ].height as number;
      const canvasWidth = this._canvas.getWidth();
      const canvasHeight = this._canvas.getHeight();
      const canvasScale = Math.min(
        canvasWidth / bottomImageWidth,
        canvasHeight / bottomImageHeight,
      );
      const rect = new fabric.Rect({
        width: item.width * canvasScale,
        height: item.height * canvasScale,
        top: item.top * canvasScale,
        left: item.left * canvasScale,
        fill: item.bottomColor,
      });
      const fontShadow = new fabric.Text(lineText, {
        fontSize: item.fontSize,
        fontFamily: font?.name || item.font.name,
        stroke: item.fontShadowColor,
        fill: item.fontShadowColor,
        strokeWidth: 8,
        textAlign: "center",
        left: (item.left + item.width / 2) * canvasScale,
        top: (item.top + item.height / 2) * canvasScale,
        originX: "center",
        originY: "center",
      });
      const fontText = new fabric.Text(lineText, {
        fontSize: item.fontSize,
        fontFamily: font?.name || item.font.name,
        fill: fontColor || item.fontColor,
        textAlign: "center",
        left: (item.left + item.width / 2) * canvasScale,
        top: (item.top + item.height / 2) * canvasScale,
        originX: "center",
        originY: "center",
      });
      const _scale = Math.min(
        Math.min(
          (item.width / (fontText.width || 1)) * canvasScale,
          (item.height / (fontText.height || 1)) * canvasScale,
        ),
        1,
      );
      const _lineText = new fabric.Group([fontShadow, fontText], {
        originX: "center",
        originY: "center",
        scaleX: _scale,
        scaleY: _scale,
      });
      const textLayout = new fabric.Group([rect, _lineText]);
      this._canvas.add(textLayout);
      this._canvas.renderAll();
    });
  };

  /** 生成 base64 */
  public toDataURL = async (
    images: Omit<ImageData, "origin">[],
    lineText?: string,
    fontColor?: string,
    font?: Font,
  ) => {
    this._canvas.clear();
    await this._renderBottomImage();
    if (lineText && this.isLettering) {
      await this._renderLineText(lineText, fontColor, font);
    }
    await this._renderImages(images);
    const url = this._canvas.toDataURL({ format: "jpeg" });
    return url;
  };
}

export default Pattern;
